import { defineQuery, type State, type System } from 'vibegame';
import { Transform, WorldTransform } from 'vibegame/transforms';
import { Line } from 'vibegame/line';
import { Word } from 'vibegame/text';
import { Border } from './components';

const borderQuery = defineQuery([Border, Transform]);

function createLineEntity(state: State, color: number, thickness: number): number {
  const eid = state.createEntity();
  state.addComponent(eid, Transform, { posX: 0, posY: 0, posZ: 0 });
  state.addComponent(eid, Line, {
    offsetX: 0,
    offsetY: 0,
    offsetZ: 0,
    color,
    thickness,
    visible: 1,
  });
  return eid;
}

export const BorderSetupSystem: System = {
  group: 'simulation',
  update(state) {
    for (const eid of borderQuery(state.world)) {
      if (Border.initialized[eid]) continue;

      const color = Border.color[eid];
      const thickness = Border.thickness[eid];

      Border.lineTop[eid] = createLineEntity(state, color, thickness);
      Border.lineRight[eid] = createLineEntity(state, color, thickness);
      Border.lineBottom[eid] = createLineEntity(state, color, thickness);
      Border.lineLeft[eid] = createLineEntity(state, color, thickness);

      Border.initialized[eid] = 1;
    }
  },
};

export const BorderLayoutSystem: System = {
  group: 'simulation',
  after: [BorderSetupSystem],
  update(state) {
    for (const eid of borderQuery(state.world)) {
      if (!Border.initialized[eid]) continue;

      let width: number;
      let height: number;

      if (state.hasComponent(eid, Word)) {
        width = Word.width[eid];
        height = Word.fontSize[eid];
      } else {
        console.warn('border: bounds not implemented for non-text entities');
        continue;
      }

      if (width === 0) continue;

      const posX = WorldTransform.posX[eid];
      const posY = WorldTransform.posY[eid];
      const posZ = WorldTransform.posZ[eid];
      const padding = Border.padding[eid];
      const transition = Math.max(0, Math.min(1, Border.transition[eid]));

      const halfWidth = width / 2 + padding;
      const halfHeight = height / 2 + padding;

      const left = posX - halfWidth;
      const right = posX + halfWidth;
      const top = posY + halfHeight;
      const bottom = posY - halfHeight;
      const boxWidth = right - left;
      const boxHeight = top - bottom;

      const lineTop = Border.lineTop[eid];
      const lineRight = Border.lineRight[eid];
      const lineBottom = Border.lineBottom[eid];
      const lineLeft = Border.lineLeft[eid];

      Transform.posX[lineTop] = left;
      Transform.posY[lineTop] = top;
      Transform.posZ[lineTop] = posZ;
      Line.offsetX[lineTop] = boxWidth * transition;
      Line.offsetY[lineTop] = 0;

      Transform.posX[lineRight] = right;
      Transform.posY[lineRight] = top;
      Transform.posZ[lineRight] = posZ;
      Line.offsetX[lineRight] = 0;
      Line.offsetY[lineRight] = -boxHeight * transition;

      Transform.posX[lineBottom] = left;
      Transform.posY[lineBottom] = bottom;
      Transform.posZ[lineBottom] = posZ;
      Line.offsetX[lineBottom] = boxWidth * transition;
      Line.offsetY[lineBottom] = 0;

      Transform.posX[lineLeft] = left;
      Transform.posY[lineLeft] = bottom;
      Transform.posZ[lineLeft] = posZ;
      Line.offsetX[lineLeft] = 0;
      Line.offsetY[lineLeft] = boxHeight * transition;
    }
  },
};

export const BorderCleanupSystem: System = {
  group: 'simulation',
  after: [BorderLayoutSystem],
  update(state) {
    for (const eid of borderQuery(state.world)) {
      if (!state.exists(eid)) {
        const lineTop = Border.lineTop[eid];
        const lineRight = Border.lineRight[eid];
        const lineBottom = Border.lineBottom[eid];
        const lineLeft = Border.lineLeft[eid];

        if (state.exists(lineTop)) state.destroyEntity(lineTop);
        if (state.exists(lineRight)) state.destroyEntity(lineRight);
        if (state.exists(lineBottom)) state.destroyEntity(lineBottom);
        if (state.exists(lineLeft)) state.destroyEntity(lineLeft);
      }
    }
  },
};
